import {
  ExerciseCanvas,
  ExerciseVideoCard,
  roundRect,
  drawLimb,
  drawPersonHead,
  drawSneaker,
  drawCardFrame,
  drawChecklistPanel,
  drawPhasePill,
} from "@/components/we360/ExerciseCanvas";
import { useUserProfile } from "@/contexts/UserProfileContext";

function useIsFemale(sexProp) {
  let ctxSex = null;
  try {
    const ctx = useUserProfile();
    ctxSex = ctx?.profile?.sexo || ctx?.profile?.genero;
  } catch {
    // Allow rendering outside UserProfileProvider in isolated unit tests
  }
  const resolved = sexProp || ctxSex || "";
  return /^femenino|^mujer|^female|^f$/i.test(String(resolved).trim());
}

export function ActivePersonSVG({ sex }) {
  const isFemale = useIsFemale(sex);
  return (
    <ExerciseVideoCard
      testId="f3-welcome-canvas"
      videoSrc={
        isFemale
          ? "/videos/exercises/we360_ai_caminata_f.mp4"
          : "/videos/exercises/we360_ai_caminata.mp4"
      }
      title="Evaluación de Actividad Física (IPAQ)"
      subtitle="Registra tu movimiento de los últimos 7 días (mínimo 10 min seguidos)"
      badgeText="Últimos 7d"
      checklistTitle="Dimensiones IPAQ"
      tips={[
        { label: "Vigorosa (8.0 MET)", sub: "Correr, HIIT, pesas fuertes", active: true },
        { label: "Moderada (4.0 MET)", sub: "Ciclismo suave, baile, tenis", active: true },
        { label: "Caminata (3.3 MET)", sub: "Traslado o paseo ≥ 10 min", active: true },
      ]}
      phaseLabel="Solo cuenta actividades que duraron al menos 10 minutos continuos"
      isHighlight={true}
    />
  );
}

export function VigorousSVG({ sex }) {
  const isFemale = useIsFemale(sex);
  return (
    <ExerciseVideoCard
      testId="f3-vigorous-canvas"
      videoSrc={
        isFemale
          ? "/videos/exercises/we360_ai_vigorosa_f.mp4"
          : "/videos/exercises/we360_ai_vigorosa.mp4"
      }
      title="Actividad Física Vigorosa (8.0 MET)"
      subtitle="Esfuerzo físico intenso que acelera mucho tu respiración y pulso"
      badgeText="8.0 MET"
      checklistTitle="Criterios Vigorosos"
      tips={[
        { label: "Respiración muy fuerte", sub: "Cuesta hablar seguido", active: true },
        { label: "Ejemplos comunes", sub: "Pesas fuertes, HIIT, correr", active: true },
        { label: "Bloques ≥ 10 min", sub: "Tiempo continuo por sesión", active: true },
      ]}
      phaseLabel="Máximo esfuerzo: levantamiento de pesas, HIIT, carrera o aeróbicos"
      isHighlight={true}
    />
  );
}

export function ModerateSVG({ sex }) {
  const isFemale = useIsFemale(sex);
  return (
    <ExerciseVideoCard
      testId="f3-moderate-canvas"
      videoSrc={
        isFemale
          ? "/videos/exercises/we360_ai_moderada_f.mp4"
          : "/videos/exercises/we360_ai_moderada.mp4"
      }
      title="Actividad Física Moderada (4.0 MET)"
      subtitle="Esfuerzo medio que acelera un poco la respiración (sin agotarte)"
      badgeText="4.0 MET"
      checklistTitle="Criterios Moderados"
      tips={[
        { label: "Respiración constante", sub: "Puedes conversar al hacerlo", active: true },
        { label: "Ejemplos comunes", sub: "Bici estática, nadar, bailar", active: true },
        { label: "No incluye caminar", sub: "Caminata se registra aparte", active: true },
      ]}
      phaseLabel="Ritmo sostenido: bicicleta a paso regular, natación suave o dobles de tenis"
      isHighlight={true}
    />
  );
}

export function F3WalkingSVG({ sex }) {
  const isFemale = useIsFemale(sex);
  return (
    <ExerciseVideoCard
      testId="f3-walking-canvas"
      videoSrc={
        isFemale
          ? "/videos/exercises/we360_ai_caminata_f.mp4"
          : "/videos/exercises/we360_ai_caminata.mp4"
      }
      title="Caminata Continua (3.3 MET)"
      subtitle="Caminatas por traslado, trabajo, compras o recreación (≥ 10 min)"
      badgeText="10+ min"
      checklistTitle="Guía de Caminata"
      tips={[
        { label: "Mínimo 10 min", sub: "Tramos continuos sin parar", active: true },
        { label: "Todo propósito", sub: "Trabajo, casa o ejercicio", active: true },
        { label: "Suma diaria", sub: "Total de minutos por día", active: true },
      ]}
      phaseLabel="Incluye caminatas hacia el trabajo, paseos o desplazamientos diarios"
      isHighlight={true}
    />
  );
}

export function SittingSVG() {
  return (
    <ExerciseCanvas
      cycleDurationMs={2800}
      testId="f3-sitting-canvas"
      renderFrame={(ctx, t) => {
        const breath = Math.sin(t * Math.PI * 2) * 2;

        drawCardFrame(ctx, {
          title: "Tiempo Sentado (Conducta Sedentaria)",
          subtitle: "Horas promedio al día sentado/a en escritorio, transporte o descanso",
          badgeText: "Horas/día",
          progress: t,
          isHighlight: false,
        });

        const groundY = 334;
        ctx.strokeStyle = "#D5CBB8";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(45, groundY);
        ctx.lineTo(355, groundY);
        ctx.stroke();

        // Ergonomic chair
        const chairX = 148;
        const seatY = 266;
        ctx.strokeStyle = "#71644C";
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(chairX - 18, seatY - 72);
        ctx.lineTo(chairX - 14, seatY);
        ctx.lineTo(chairX + 42, seatY);
        ctx.moveTo(chairX + 14, seatY);
        ctx.lineTo(chairX + 14, groundY);
        ctx.moveTo(chairX - 8, groundY);
        ctx.lineTo(chairX + 36, groundY);
        ctx.stroke();

        // Desk + Laptop on right of left stage
        const deskX = 235;
        const deskY = 232;
        roundRect(ctx, deskX, deskY, 96, 8, 4);
        ctx.fillStyle = "#D5CBB8";
        ctx.fill();
        ctx.strokeStyle = "#B5A998";
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(deskX + 74, deskY + 8);
        ctx.lineTo(deskX + 74, groundY);
        ctx.stroke();

        // Laptop
        ctx.strokeStyle = "#263238";
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.moveTo(deskX + 14, deskY - 2);
        ctx.lineTo(deskX + 52, deskY - 2);
        ctx.lineTo(deskX + 64, deskY - 36);
        ctx.stroke();

        // Seated person
        const hipX = chairX + 8;
        const hipY = seatY - 12;
        const kneeX = hipX + 54;
        const kneeY = hipY + 4;
        const ankleX = kneeX + 4;
        const ankleY = groundY - 8;

        const shoulderX = hipX + 4;
        const shoulderY = hipY - 66 + breath;
        const headX = shoulderX + 8;
        const headY = shoulderY - 24;

        drawLimb(ctx, hipX, hipY, kneeX, kneeY, 13.5, 10.5, "#263238");
        drawLimb(ctx, kneeX, kneeY, ankleX, ankleY, 9.5, 6.5, "#F0C49E");
        drawSneaker(ctx, ankleX, ankleY, "#00628F");

        drawLimb(ctx, hipX, hipY, shoulderX, shoulderY, 14.5, 16, "#00628F");
        drawPersonHead(ctx, shoulderX, shoulderY, headX, headY, 1);

        const elbowX = shoulderX + 24;
        const elbowY = shoulderY + 34;
        const handX = deskX + 28;
        const handY = deskY - 6;
        drawLimb(ctx, shoulderX, shoulderY, elbowX, elbowY, 8.5, 7, "#0077AC");
        drawLimb(ctx, elbowX, elbowY, handX, handY, 6.5, 5, "#F0C49E");

        drawChecklistPanel(ctx, {
          title: "Qué Incluir",
          tips: [
            { label: "Jornada laboral", sub: "Escritorio, estudio o reuniones", active: true },
            { label: "Transporte y casa", sub: "Auto, bus, TV, computador", active: true },
            { label: "Pausas activas", sub: "Levántate cada 60 minutos", active: t > 0.5 },
          ],
        });

        drawPhasePill(ctx, {
          label: "Estima el total de horas sentado/a en un día hábil típico",
          isHighlight: false,
        });
      }}
    />
  );
}

export function F3SummarySVG({ complete }) {
  return (
    <ExerciseCanvas
      cycleDurationMs={2800}
      testId="f3-summary-canvas"
      renderFrame={(ctx, t) => {
        const pulse = (1 - Math.cos(t * Math.PI * 2)) / 2;

        drawCardFrame(ctx, {
          title: "Resumen IPAQ de Actividad Física (F3)",
          subtitle: complete
            ? "¡Evaluación IPAQ lista! Revisa tu gasto energético semanal"
            : "Completa tus datos para calcular tu nivel de actividad física",
          badgeText: complete ? "IPAQ Listo" : "Resumen",
          progress: complete ? 1 : t,
          isHighlight: Boolean(complete),
        });

        const cx = 205;
        const cy = 215;

        ctx.beginPath();
        ctx.arc(cx, cy, 68 + pulse * 6, 0, Math.PI * 2);
        ctx.fillStyle = complete ? "rgba(21,115,70,0.10)" : "rgba(249,160,27,0.12)";
        ctx.fill();

        ctx.beginPath();
        ctx.arc(cx, cy, 52, 0, Math.PI * 2);
        ctx.fillStyle = complete ? "#157346" : "#F9A01B";
        ctx.fill();

        ctx.beginPath();
        ctx.arc(cx, cy, 42, 0, Math.PI * 2);
        ctx.fillStyle = "#FFFFFF";
        ctx.fill();

        ctx.fillStyle = complete ? "#157346" : "#D97706";
        ctx.font = '800 16px "Plus Jakarta Sans", sans-serif';
        ctx.fillText("IPAQ", cx - 20, cy + 6);

        drawChecklistPanel(ctx, {
          title: "Balance Semanal",
          tips: [
            { label: "Gasto MET Total", sub: "Suma de 3 intensidades", active: true },
            { label: "Categoría OMS/IPAQ", sub: "Bajo, Moderado o Alto", active: true },
            { label: "Índice Sedentario", sub: "Horas sentado/a por día", active: Boolean(complete) },
          ],
        });

        drawPhasePill(ctx, {
          label: complete
            ? "✓ Presiona «Enviar evaluación» para sincronizar tu perfil IPAQ"
            : "Revisa el desglose de tus resultados antes de guardar",
          isHighlight: Boolean(complete),
        });
      }}
    />
  );
}
